import React, { useState, useEffect } from "react";
import { IntlProvider } from "react-intl";
import { MathJaxContext } from "better-react-mathjax";

import { initDatadogInteractiveInfographics } from "@labxchange/util-datadog";
import { ImageDiscoveryCanvas } from "@learningblocks/interactives/interactive-infographic-components/lib/ImageDiscoveryCanvas";
import { Translations as fetchComponentTranslations } from "@learningblocks/shared-components/lib/Translations";
import { ImageHotspotProviders } from "@learningblocks/shared-components";
import { MediaType } from "@learningblocks/storybook-components/lib/MediaType";
import { HighlightProvider } from "@learningblocks/shared-components/lib/HighlightProvider";
import { QuestionProvider } from "@labxchange/ui-components/lib/QuestionProvider";

import { calculateTooltipPosition } from "@learningblocks/shared-components/lib/HighlightModal/HighlightModal.utils";

import {
  UsersnapProvider,
  loadUsersnap,
  USERSNAP_WIDGETS,
} from "@labxchange/util-usersnap";

// Define the type for sceneData to match ImageDiscoveryCanvas expectations
interface SceneData {
  mainView: number;
  views: object;
  hotspots: object;
  entities: any[];
  transitionSpeed: number;
  usersnap?: any;
  [key: string]: any;
}

const fetchSceneData = async (): Promise<SceneData> => {
  const sceneDataPath = `${window.CONTENT_BASE_URL}scene.json`;
  const response = await fetch(sceneDataPath);

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    const projectPath = window.location.pathname
      .split("/")
      .filter(Boolean)
      .pop();
    throw new Error(
      `Scene data not found. The image hotspot project ${projectPath} might not exist. Please check the URL or contact support.`
    );
  }

  if (!response.ok) {
    throw new Error(`Failed to load scene data: ${response.status}`);
  }

  return response.json();
};

export function SceneWrap(): React.JSX.Element {
  const [lang, setLang] = useState("en");
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [sceneData, setSceneData] = useState<SceneData | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjectTranslations = async (
    lang: string
  ): Promise<Record<string, string>> => {
    try {
      const response = await fetch(
        `${window.CONTENT_BASE_URL}locales/${lang}/translation.json`
      );
      if (!response.ok) {
        console.warn(
          `Failed to load translations for ${lang}, falling back to en`
        );
        if (lang !== "en") {
          const fallbackResponse = await fetch(
            `${window.CONTENT_BASE_URL}locales/en/translation.json`
          );
          return fallbackResponse.json();
        }
        return {};
      }
      return response.json();
    } catch (error) {
      console.error(`Error loading translations for ${lang}:`, error);
      return {};
    }
  };

  const getTranslations = async (
    lang: string | null
  ): Promise<{ lang: string; messages: Record<string, string> }> => {
    const projectMessages = (await fetchProjectTranslations(
      lang || "en"
    )) as Record<string, string>;
    const componentMessages = (await fetchComponentTranslations(
      lang || "en"
    )) as {
      default: Record<string, string>;
    };
    return {
      lang: lang || "en",
      messages: { ...projectMessages, ...componentMessages.default },
    };
  };

  const updateLanguageFromQueryParam = async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lng");
    const translations = await getTranslations(lang);
    setLang(translations.lang);
    setMessages(translations.messages);
  };

  useEffect(() => {
    initDatadogInteractiveInfographics(
      window.CONTENT_BASE_URL.split("/").filter(Boolean).pop()
    );
    updateLanguageFromQueryParam();

    fetchSceneData()
      .then((data) => setSceneData(data))
      .catch((err) => {
        console.error("Error loading scene data:", err);
        setError(err);
      });
  }, []);

  if (error) {
    throw error;
  }

  if (!sceneData) {
    return (
      <div className="fallback-loading">
        <div className="loading-animation1">.</div>
        <div className="loading-animation2">.</div>
        <div className="loading-animation3">.</div>
      </div>
    );
  }

  return (
    <MathJaxContext>
      <MediaType.Provider>
        <HighlightProvider.Component
          calculateTooltipPosition={calculateTooltipPosition}
        >
          <UsersnapProvider
            onLoadCallback={(event: any, api: any) => {
              const widgets = [
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_INTERNAL_FEEDBACK_STAGE,
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_USER_FEEDBACK_PROD,
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_SATISFACTION_SCORE,
              ];

              // Add custom widget from sceneData if it exists
              if (sceneData?.usersnap) {
                widgets.push(sceneData.usersnap);
              }

              loadUsersnap(event, api, widgets);
            }}
          >
            <div role="main" className="canvas-standalone-container">
              <ImageHotspotProviders>
                <IntlProvider locale={lang} messages={messages}>
                  <QuestionProvider>
                    <ImageDiscoveryCanvas
                      sceneData={sceneData}
                      preview={false}
                      standalone={true}
                    />
                  </QuestionProvider>
                </IntlProvider>
              </ImageHotspotProviders>
            </div>
          </UsersnapProvider>
        </HighlightProvider.Component>
      </MediaType.Provider>
    </MathJaxContext>
  );
}
